// Student public profile essential-info editor container: saves profile image and keeps global header avatar in sync.
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { setInfo as setUserInfo } from "reducers/User";

import * as ImageHelper from "helpers/Image.helper";
import * as DataApi from "api/common/Data.api";
import * as PublicProfileApi from "api/student/v2/publicProfile.api";

import PublicProfileEditComponent from "./PublicProfileEssentialInfoEdit.component";
import PublicProfileImageCropModalComponent from "./PublicProfileImageCropModal.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

const PublicProfileEssentialInfoEditContainer = (props: any) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { info: myInfo } = useSelector((state: any) => state.user);

    const [studentInfo, setStudentInfo] = useState<any>({});
    const [cropObjectUrl, setCropObjectUrl] = useState<string | null>(null);
    const pickedFileNameRef = useRef("");

    const [imageInfo, setImageInfo] = useState<any>({});

    const [profileParams, setProfileParams] = useState<any>({});

    const [isOnNetworking, setIsOnNetworking] = useState(false);

    useEffect(() => {
        getInfo();
    }, []);

    const getInfo = async () => {
        const result = await PublicProfileApi.getEssentialInfo();

        if (result?.info) {
            const { info } = result;
            setStudentInfo(info);
        }
    };

    const onChangeInput = (componentName: string, value: any) => {
        console.log("UpdateMyContainer:onChangeInput", componentName, value);

        if (componentName === "countryCode") {
            setProfileParams({ ...profileParams, countryCode: value });
        } else if (componentName === "firstName") {
            setProfileParams({ ...profileParams, firstName: value });
        } else if (componentName === "lastName") {
            setProfileParams({ ...profileParams, lastName: value });
        } else if (componentName === "birthday") {
            setProfileParams({ ...profileParams, birthday: value });
        } else if (componentName === "genderTypeCode") {
            setProfileParams({ ...profileParams, genderTypeCode: value });
        } else if (componentName === "nickname") {
            setProfileParams({ ...profileParams, nickname: value });
        }
    };

    const isDisabled = async () => {
        if (!profileParams.countryCode) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errCountrySelection"));
            return true;
        } else if (!profileParams.firstName) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errFirstName"));
            return true;
        } else if (!profileParams.lastName) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errLastName"));
            return true;
        } else if (!profileParams.birthday) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errBirthdaySelection"));
            return true;
        } else if (!imageInfo?.id) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errProfileImageSelection"));
            return true;
        } else if (!profileParams.genderTypeCode) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignup.errGenderSelection"));
            return true;
        } else {
            return false;
        }
    };

    const updateInfo = async () => {
        if (!(await isDisabled())) {
            setIsOnNetworking(true);
            profileParams.profileImageId = imageInfo?.id || null;

            await PublicProfileApi.updateEssentialInfo(profileParams)
                .then((res: any) => {
                    if (res) {
                        // header avatar uses redux user.info.profileImage; refresh it after save
                        const nextUrl = imageInfo?.url;
                        if (nextUrl) {
                            dispatch(setUserInfo({ ...myInfo, profileImage: nextUrl }));
                        }
                        navigate("/student/profile/public");
                    }
                })
                .catch((err: any) => {
                    console.log("UpdateMyContainer:updateInfo", err);
                    alert("Error Occurred");
                })
                .finally(() => {
                    setIsOnNetworking(false);
                });
        }
    };

    const closeCropModal = () => {
        if (cropObjectUrl) {
            URL.revokeObjectURL(cropObjectUrl);
        }
        setCropObjectUrl(null);
    };

    const onProfileImageFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const file = evt.target.files?.[0];
        evt.target.value = "";
        if (!file) return;
        if (!/^image\/(jpeg|jpg|png)$/i.test(file.type)) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.txtCropInvalidFormat"));
            return;
        }
        pickedFileNameRef.current = file.name;
        setCropObjectUrl(URL.createObjectURL(file));
    };

    const onCropModalApply = async (blob: Blob) => {
        const rawName = pickedFileNameRef.current || "profile";
        const base = rawName.replace(/\.[^.]+$/i, "") || "profile";
        const file = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
        closeCropModal();
        setIsOnNetworking(true);
        try {
            const preview = await ImageHelper.getImageInfo(file);
            if (!preview) {
                alert("Please choose image file");
                return;
            }
            const result = await DataApi.uploadImage(file);
            setImageInfo({ id: result.id, url: preview.url });
        } catch (err) {
            console.log("PublicProfileEssentialInfoEditContainer:onCropModalApply", err);
        } finally {
            setIsOnNetworking(false);
        }
    };

    useEffect(() => {
        if (studentInfo?.id) {
            setImageInfo(studentInfo.profileImageInfo);

            setProfileParams({
                countryCode: studentInfo.countryCodeInfo.code,
                firstName: studentInfo.firstName,
                lastName: studentInfo.lastName,
                birthday: studentInfo.birthday,
                genderTypeCode: studentInfo.genderTypeCodeInfo.code,
                nickname: studentInfo.nickname,
            });
        }
    }, [studentInfo]);

    return (
        <>
            <PublicProfileEditComponent
                id={studentInfo?.id}
                profileParams={profileParams}
                imageInfo={imageInfo}
                setImageInfo={setImageInfo}
                isOnNetworking={isOnNetworking}
                onProfileImageFileChange={onProfileImageFileChange}
                onChangeInput={onChangeInput}
                onClickBtnUpdateInfo={updateInfo}
            />
            {cropObjectUrl && (
                <PublicProfileImageCropModalComponent
                    imageSrc={cropObjectUrl}
                    onClose={closeCropModal}
                    onApply={onCropModalApply}
                />
            )}
        </>
    );
};

export default PublicProfileEssentialInfoEditContainer;
